import React, { useState, useEffect, useRef, useContext } from "react";
import { StyleSheet, ScrollView, TouchableOpacity, Text as RNText, Modal as RNModal } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { View } from "../../components/shared/Themed";
import { Text, FAB, Provider as PaperProvider, DefaultTheme } from "react-native-paper";
import { DatePickerModal, registerTranslation } from "react-native-paper-dates";

registerTranslation("en-AU", {
  save: "Save",
  selectSingle: "Select date",
  selectMultiple: "Select dates",
  selectRange: "Select period",
  notAccordingToDateFormat: (inputFormat) =>
    `Date format must be ${inputFormat}`,
  mustBeHigherThan: (date) => `Must be later than ${date}`,
  mustBeLowerThan: (date) => `Must be earlier than ${date}`,
  mustBeBetween: (startDate, endDate) =>
    `Must be between ${startDate} - ${endDate}`,
  dateIsDisabled: "Day is not allowed",
  previous: "Previous",
  next: "Next",
  typeInDate: "Type in date",
  pickDateFromCalendar: "Pick date from calendar",
  close: "Close",
  hour: "Hour",
  minute: "Minute",
  startDate: "Start Date (DD/MM/YYYY)",
  endDate: "End Date (DD/MM/YYYY)",
  dateFormat: "DD/MM/YYYY",
});

import { Ionicons } from "@expo/vector-icons";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import { default as Modal } from "../../components/custom/Modal/index";
import Spinner from "../../components/shared/Spinner";
import useColorScheme from "../../hooks/useColorScheme";
import oktaConfig from "../../configs/okta";
import _ from "lodash";

import {
  deleteContainerById,
  getAllContainers,
} from "../../services/containersService";
import { headers, mdHeaders, smHeaders } from "./utils/containerHeaders";
import {
  nestedHeaders as itemsHeader,
  mdNestedHeaders as itemsMdHeaders,
  smNestedHeaders as itemsSmHeaders,
} from "./utils/itemsHeader";
import { containersModal } from "../../models/containersModel";
import {
  compareArraysToHaveAnIdenticalArray,
  filterBasedOnAnyKey,
} from "../../utils/searchInArrays";
import { getAllItems } from "../../services/itemsService";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { useToast } from "react-native-toast-notifications";
import { useSelector } from "react-redux";
import { handleTableLoader } from "../../utils/common";
import { itemsModal } from "../../models/itemsModel";
import { sortColumns } from "../../utils/sortColumns";
import { ContainersDispatcher } from "../../redux/dispatcher/containersDispatcher";
import { actionTypesContainers } from "../../redux/constants/actionTypesContainers";
import { ContainerSearchDisplaySwitch } from "./utils/ContainerSearchDisplaySwitch";
import { useTranslation } from "../../utils/useTranslation";

export default function ContainerManagementScreen({ navigation }: any) {
  const toast = useToast();
  const { t } = useTranslation();
  const scrollRef = useRef<ScrollView>(null);
  const isFocused = useIsFocused();
  const colorScheme = useColorScheme();

  const columnsToNotHaveThousandsSeparator: Array<string> = ["Packing #"];
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [filterOpen, setFilterOpen] = useState<"status" | "priority" | "etd" | "eta" | null>(null);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [priorityFilters, setPriorityFilters] = useState<string[]>([]);
  const [etdFilter, setEtdFilter] = useState("All");
  const [etaFilter, setEtaFilter] = useState("All");
  const [itemsModalVisible, setItemsModalVisible] = useState(false);
  const [selectedContainerForItems, setSelectedContainerForItems] = useState<containersModal | null>(null);

  const [etdRangePickerOpen, setEtdRangePickerOpen] = useState(false);
  const [etaRangePickerOpen, setEtaRangePickerOpen] = useState(false);
  const [customEtdRange, setCustomEtdRange] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({ startDate: undefined, endDate: undefined });
  const [customEtaRange, setCustomEtaRange] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({ startDate: undefined, endDate: undefined });

  const formatShortDate = (d: Date) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getEtdDateRangeLabel = () => {
    if (customEtdRange.startDate && customEtdRange.endDate) {
      return `${formatShortDate(customEtdRange.startDate)} - ${formatShortDate(customEtdRange.endDate)}`;
    }
    return t("all");
  };

  const getEtaDateRangeLabel = () => {
    if (customEtaRange.startDate && customEtaRange.endDate) {
      return `${formatShortDate(customEtaRange.startDate)} - ${formatShortDate(customEtaRange.endDate)}`;
    }
    return t("all");
  };

  const [containersPageLevelList, setContainersPageLevelList] = useState<
    Array<containersModal>
  >([]);

  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );

  const { userRoles } = useSelector((state: any) => state.configurations);
  const { allowedRolesForContainers, containersList, isContainersListLoaded } =
    useSelector((state: any) => state.containers);

  const { itemsList } = useSelector((state: any) => state.items);

  const isUserAdmin = userRoles.some((role: string) =>
    allowedRolesForContainers.includes(role)
  );

  const [selected, setSelected] = useState<containersModal>(
    {} as unknown as containersModal
  );

  //Table & Nested Table Headers
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );

  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      itemsHeader
    );

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(true);
  const [currentSearchText, setCurrentSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [isFabOpen, setFabOpen] = useState<Record<string, boolean>>({
    open: false,
  });

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedContainerList: Array<containersModal> = sortColumns(
      containersPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setContainersPageLevelList(_sortedContainerList);
  };

  /**
   * TODO: NESTED TABLE SORTED Is Disabled,
   * enable when sorting on nested table feature is requested
   */
  // const onHandleNestedSortColumn = (
  //   columnKey: string,
  //   columnParentIdentifier: string
  // ) => {
  //   const _sortedItemsList: Array<itemsModal> = sortColumns(
  //     nestedItemsPageLevelList,
  //     columnKey,
  //     nestedTableHeaders,
  //     setNestedTableHeaders
  //   );

  //   setNestedItemsPageLevelList(_sortedItemsList);
  // };

  const onHandleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  const onFabOpenChange = ({ open }: { open: boolean }) => setFabOpen({ open });

  const onHandleRowClick = (row: containersModal) => {
    navigation.navigate("ManageContainers", {
      screen: "ManageContainers",
      mode: "update",
      container: JSON.stringify(row),
    });
  };

  const displayTableLoader = (setIsSearched: (e: boolean) => void) => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = (setIsSearched: (e: boolean) => void) => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleRemoveRow = (containerId: string) => {
    const _containersList = containersPageLevelList;
    const alteredContainersList = _containersList.filter(
      (container) => container.container_number !== containerId
    );

    ContainersDispatcher(
      actionTypesContainers.ListOfContainers,
      alteredContainersList
    );
  };

  const onHandleDeleteRow = (containerId: string | number) => {
    if (confirm("Do you want to delete this container?")) {
      displayTableLoader(setIsSearched);

      deleteContainerById(containerId as string).then((response) => {
        if (response?.isSuccessfull) {
          onHandleRemoveRow(containerId as string);
          toast.show("Container deleted successfully", {
            type: "success",
          });
        } else {
          toast.show("Error: container not deleted. Try again", {
            type: "danger",
          });
        }
      });

      fadeTableLoader(setIsSearched);
    }
  };

  const onHandleSearch = (searchText: string) => {
    setCurrentSearchText(searchText);
    displayTableLoader(setIsSearched);

    let searchedContainers: Array<containersModal> = [];
    if (searchText.trim() !== "") {
      /**
       * When SearchBy: Containers, enter into if block
       * otherwise go for else block
       */
      if (!_isDisplayViewOn) {
        const filteredContainers = filterBasedOnAnyKey(
          extractIdenticalContainers(),
          searchText,
          ["container_number", "deliver_to"]
        );

        searchedContainers = feedItemsInContainers(filteredContainers);
      } else {
        /**
         * Steps to search for items within containers
         * 1: Extract all items from containers & union them
         * 2: Search by keyword in the list of items extracted from containers
         * 3: Group by container_number on the searchedItems
         * 4: Remove the duplicates from array of container_numbers
         * 5: filter the unique containers from the identicalFeededContainersWithItems()
         *    & return the list of containers with items matching the search text
         */

        //Step 1
        const flattenItems = identicalFeededContainersWithItems().flatMap(container =>
          container.items.map(item => ({ ...item, deliver_to: container.deliver_to }))
        );

        //Step 2
        const searchedItems = filterBasedOnAnyKey(flattenItems, searchText, [
          "container_number",
          "ship_via",
          "deliver_to",
          "item_code",
          "description",
          "packing_list",
          "packing_list_no",
          "state",
          "customer"
        ]);

        //Step 3
        const groupedContainers = Object.keys(
          _.groupBy(searchedItems, "container_number")
        );

        //Step 4
        const uniqueContainers = _.uniq(groupedContainers);

        //Step 5
        const filteredContainers = identicalFeededContainersWithItems().filter(
          (container) => uniqueContainers.includes(container.container_number)
        );

        searchedContainers = filteredContainers;
      }

      setContainersPageLevelList(searchedContainers);
    }

    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  };

  const onHandleScrollToBase = () => {
    scrollRef.current?.scrollTo({
      x: 0,
      y: 0,
      animated: true,
    });
  };

  const ModalContent = (props: any) => {
    const _excludedKeys = [
      "_id",
      "__v",
      "created_Date",
      "created_by",
      "container_number",
      "items",
    ];

    return (
      <Modal.ModalContent
        {...props}
        title={selected.container_number}
        icon="cube-outline"
        childern={
          <Modal.ModalBody
            listData={selected}
            excludedKeys={_excludedKeys}
            renameList={headers}
          />
        }
      />
    );
  };


  const formatDateShort = (dateStr?: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${("0" + d.getDate()).slice(-2)}/${("0" + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`;
  };

  const statusColorMap: Record<string, { bg: string; text: string; dot: string }> = {
    Delivered: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981" },
    "In Transit": { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
    Scheduled: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
    Shipped: { bg: "#E1F0FA", text: "#1D6FA5", dot: "#2F95DC" },
  };

  const priorityColorMap: Record<string, string> = {
    Urgent: "#EF4444",
    Priority: "#F59E0B",
    Normal: "#10B981",
  };

  const MobileContainerCard = ({ container }: { container: containersModal }) => {
    const sc = statusColorMap[container.status] || { bg: "#F1F5F9", text: "#475569", dot: "#94A3B8" };
    const priorityColor = priorityColorMap[container.priority] || "#94A3B8";
    const progress = typeof container.progress === "number" ? container.progress : 0;

    return (
      <View style={cardStyles.card}>
        {/* Header */}
        <View style={cardStyles.cardHeader}>
          <View style={cardStyles.cardHeaderLeft}>
            <View style={cardStyles.containerIconWrap}>
              <Ionicons name="cube-outline" size={18} color="#2F95DC" />
            </View>
            <RNText style={cardStyles.containerNumber}>{container.container_number}</RNText>
          </View>
          <View style={[cardStyles.statusBadge, { backgroundColor: sc.bg }]}>
            <View style={[cardStyles.statusDot, { backgroundColor: sc.dot }]} />
            <RNText style={[cardStyles.statusText, { color: sc.text }]}>{container.status || "Unknown"}</RNText>
          </View>
        </View>

        <View style={cardStyles.divider} />

        {/* Date Timeline */}
        <View style={cardStyles.dateRow}>
          <View style={cardStyles.dateBlock}>
            <RNText style={cardStyles.dateLabel}>ETD</RNText>
            <RNText style={cardStyles.dateValue}>{formatDateShort(container.etd)}</RNText>
          </View>
          <Ionicons name="arrow-forward" size={14} color="#CBD5E1" />
          <View style={cardStyles.dateBlock}>
            <RNText style={cardStyles.dateLabel}>ETA Port</RNText>
            <RNText style={cardStyles.dateValue}>{formatDateShort(container.eta_port)}</RNText>
          </View>
          <Ionicons name="arrow-forward" size={14} color="#CBD5E1" />
          <View style={cardStyles.dateBlock}>
            <RNText style={cardStyles.dateLabel}>ETA Store</RNText>
            <RNText style={cardStyles.dateValue}>{formatDateShort(container.eta_store)}</RNText>
          </View>
        </View>

        {/* Meta chips */}
        <View style={cardStyles.metaRow}>
          {container.deliver_to ? (
            <View style={cardStyles.metaChip}>
              <Ionicons name="location-outline" size={11} color="#6B7280" />
              <RNText style={cardStyles.metaChipText}>{container.deliver_to}</RNText>
            </View>
          ) : null}
          {container.type ? (
            <View style={cardStyles.metaChip}>
              <Ionicons name="resize-outline" size={11} color="#6B7280" />
              <RNText style={cardStyles.metaChipText}>{container.type} · {container.size}</RNText>
            </View>
          ) : null}
          {container.importer ? (
            <View style={cardStyles.metaChip}>
              <Ionicons name="business-outline" size={11} color="#6B7280" />
              <RNText style={cardStyles.metaChipText}>{container.importer}</RNText>
            </View>
          ) : null}
        </View>



        {/* Footer */}
        <View style={cardStyles.cardFooter}>
          <View style={[cardStyles.priorityBadge, { borderColor: priorityColor }]}>
            <View style={[cardStyles.priorityDot, { backgroundColor: priorityColor }]} />
            <RNText style={[cardStyles.priorityText, { color: priorityColor }]}>{container.priority || "Normal"}</RNText>
          </View>
          {container.items && container.items.length > 0 && (
            <TouchableOpacity
              style={cardStyles.itemsCount}
              onPress={() => {
                setSelectedContainerForItems(container);
                setItemsModalVisible(true);
              }}
            >
              <Ionicons name="layers-outline" size={12} color="#2F95DC" />
              <RNText style={cardStyles.itemsCountText}>{container.items.length} item{container.items.length !== 1 ? "s" : ""}</RNText>
              <Ionicons name="chevron-forward" size={11} color="#2F95DC" />
            </TouchableOpacity>
          )}
          <View style={cardStyles.cardActions}>
            <TouchableOpacity
              style={cardStyles.viewBtn}
              onPress={() => {
                setSelected(container);
                setModalVisible(true);
              }}
            >
              <Ionicons name="eye-outline" size={15} color="#2F95DC" />
            </TouchableOpacity>
            {isUserAdmin && (
              <TouchableOpacity
                style={cardStyles.editBtn}
                onPress={() => onHandleRowClick(container)}
              >
                <Ionicons name="create-outline" size={15} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    );
  };

  const MobileItemsModal = () => {
    const container = selectedContainerForItems;
    const items = container?.items || [];

    const statusColor = (s: string) => {
      switch ((s || "").toLowerCase()) {
        case "confirmed": return { bg: "#D1FAE5", text: "#065F46" };
        case "pending": return { bg: "#FEF3C7", text: "#92400E" };
        case "shipped": return { bg: "#DBEAFE", text: "#1E40AF" };
        default: return { bg: "#F1F5F9", text: "#475569" };
      }
    };

    return (
      <RNModal
        visible={itemsModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setItemsModalVisible(false)}
      >
        <View style={itemModalStyles.root}>
          {/* Header */}
          <View style={itemModalStyles.header}>
            <View style={itemModalStyles.headerLeft}>
              <View style={itemModalStyles.headerIconWrap}>
                <Ionicons name="cube-outline" size={18} color="#2F95DC" />
              </View>
              <View>
                <RNText style={itemModalStyles.headerTitle}>{container?.container_number}</RNText>
                <RNText style={itemModalStyles.headerSub}>{items.length} item{items.length !== 1 ? "s" : ""}</RNText>
              </View>
            </View>
            <TouchableOpacity style={itemModalStyles.closeBtn} onPress={() => setItemsModalVisible(false)}>
              <Ionicons name="close" size={20} color="#475569" />
            </TouchableOpacity>
          </View>

          {/* Items List */}
          <ScrollView style={itemModalStyles.list} contentContainerStyle={itemModalStyles.listContent} showsVerticalScrollIndicator={false}>
            {items.length === 0 ? (
              <View style={itemModalStyles.emptyState}>
                <Ionicons name="layers-outline" size={48} color="#CBD5E1" />
                <RNText style={itemModalStyles.emptyText}>No items in this container</RNText>
              </View>
            ) : (
              items.map((item: itemsModal, idx: number) => {
                const sc = statusColor((item as any).ship_status || "");
                return (
                  <View key={item.item_code + idx} style={itemModalStyles.itemCard}>
                    {/* Item header row */}
                    <View style={itemModalStyles.itemCardHeader}>
                      <View style={itemModalStyles.itemCodeWrap}>
                        <RNText style={itemModalStyles.itemCode}>{item.item_code}</RNText>
                      </View>
                      <View style={[itemModalStyles.statusBadge, { backgroundColor: sc.bg }]}>
                        <RNText style={[itemModalStyles.statusText, { color: sc.text }]}>{(item as any).ship_status || "—"}</RNText>
                      </View>
                    </View>

                    {/* Description */}
                    {item.description ? (
                      <RNText style={itemModalStyles.description} numberOfLines={2}>{item.description}</RNText>
                    ) : null}

                    {/* Detail row */}
                    <View style={itemModalStyles.detailRow}>
                      {(item as any).bp_name || item.customer ? (
                        <View style={itemModalStyles.detailChip}>
                          <Ionicons name="person-outline" size={11} color="#6B7280" />
                          <RNText style={itemModalStyles.detailChipText}>{(item as any).bp_name || item.customer}</RNText>
                        </View>
                      ) : null}
                      {item.state ? (
                        <View style={itemModalStyles.detailChip}>
                          <Ionicons name="location-outline" size={11} color="#6B7280" />
                          <RNText style={itemModalStyles.detailChipText}>{item.state}</RNText>
                        </View>
                      ) : null}
                      {item.quantity ? (
                        <View style={itemModalStyles.detailChip}>
                          <Ionicons name="layers-outline" size={11} color="#6B7280" />
                          <RNText style={itemModalStyles.detailChipText}>Qty: {item.quantity.toLocaleString()}</RNText>
                        </View>
                      ) : null}
                      {item.packing_list_no ? (
                        <View style={itemModalStyles.detailChip}>
                          <Ionicons name="document-outline" size={11} color="#6B7280" />
                          <RNText style={itemModalStyles.detailChipText}>Pkg #{item.packing_list_no}</RNText>
                        </View>
                      ) : null}
                    </View>
                  </View>
                );
              })
            )}
          </ScrollView>
        </View>
      </RNModal>
    );
  };

  const extractIdenticalContainers = () => {

    //process both containers and items list to return filtered containers list
    const _itemsList: Array<itemsModal> = itemsList;
    const groupContainers = _.groupBy(
      _itemsList,
      (item) => item.container_number
    );

    return compareArraysToHaveAnIdenticalArray(
      "container_number",
      containersList,
      Object.keys(groupContainers)
    ) as Array<containersModal>;
  };

  const feedItemsInContainers = (containers: containersModal[]) => {
    const _itemsList: Array<itemsModal> = [...itemsList];

    return containers.map((container) => {
      return {
        ...container,
        items: _itemsList.filter(
          (item: itemsModal) =>
            item.container_number === container.container_number
        ),
      };
    });
  };

  const identicalFeededContainersWithItems = (): Array<containersModal> => {
    return feedItemsInContainers(extractIdenticalContainers());
  };

  useEffect(() => {
    onHandleSearch(currentSearchText);
  }, [_isDisplayViewOn]);

  useEffect(() => {
    let isMounted: boolean = true;

    if (isMounted) {
      //To restrict fetching data from endpoint, if data is available in store
      if (containersPageLevelList.length === 0 || isRefresh) {
        displayTableLoader(setIsSearched);

        //Loading containers and items so to apply inner join on them so to filter out containers list based on items
        getAllContainers(tableHeaders);
        getAllItems(nestedTableHeaders);
      }
    }

    setTableHeaders(
      isLargeDevice ? headers : isMediumDevice ? mdHeaders : smHeaders
    );
    setNestedTableHeaders(
      isLargeDevice
        ? itemsHeader
        : isMediumDevice
          ? itemsMdHeaders
          : itemsSmHeaders
    );

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, isRefresh]);

  useEffect(() => {
    setContainersPageLevelList(identicalFeededContainersWithItems());

    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  }, [containersList, itemsList]);

  // Stats derived from the list
  const shippedCount = containersPageLevelList.filter(
    (c) => c.status?.toLowerCase() === "shipped"
  ).length;
  const scheduledCount = containersPageLevelList.filter(
    (c) => c.status?.toLowerCase() === "scheduled"
  ).length;
  const normalCount = containersPageLevelList.filter(
    (c) => c.priority?.toLowerCase() === "normal"
  ).length;
  const urgentCount = containersPageLevelList.filter(
    (c) => c.priority?.toLowerCase() === "urgent"
  ).length;
  const priorityCount = containersPageLevelList.filter(
    (c) => c.priority?.toLowerCase() === "priority"
  ).length;
  const totalItemsCount = containersPageLevelList.reduce(
    (acc, c) => acc + (c.items?.length || 0),
    0
  );

  const filteredContainersList = containersPageLevelList.filter((c) => {
    let match = true;
    if (statusFilters.length > 0 && !statusFilters.some(f => f.toLowerCase() === c.status?.toLowerCase())) {
      match = false;
    }

    if (priorityFilters.length > 0 && !priorityFilters.some(f => f.toLowerCase() === c.priority?.toLowerCase())) {
      match = false;
    }

    if (customEtdRange.startDate && customEtdRange.endDate && c.etd) {
      const cDate = new Date(c.etd);
      cDate.setHours(0, 0, 0, 0);
      if (cDate < customEtdRange.startDate || cDate > customEtdRange.endDate) {
        match = false;
      }
    }

    if (customEtaRange.startDate && customEtaRange.endDate && c.eta_port) {
      const cDate = new Date(c.eta_port);
      cDate.setHours(0, 0, 0, 0);
      if (cDate < customEtaRange.startDate || cDate > customEtaRange.endDate) {
        match = false;
      }
    }

    return match;
  });

  const toggleFilter = (
    filter: string,
    currentFilters: string[],
    setFilters: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (filter === "All") {
      setFilters([]);
    } else {
      if (currentFilters.includes(filter)) {
        setFilters(currentFilters.filter((f) => f !== filter));
      } else {
        setFilters([...currentFilters, filter]);
      }
    }
  };

  const renderMetricCards = (isMobileView: boolean) => {
    const cards = [
      <TouchableOpacity
        key="containers"
        style={[
          styles.metricCard,
          { borderTopColor: "#2F95DC" },
          isMobileView ? { width: 85, marginRight: 8 } : { flex: 1 },
          statusFilters.length === 0 && priorityFilters.length === 0 && { backgroundColor: "#2F95DC1A", borderColor: "#2F95DC" }
        ]}
        onPress={() => {
          setStatusFilters([]);
          setPriorityFilters([]);
          setFilterOpen(null);
        }}
      >
        <RNText style={[styles.metricNumber, { color: "#2F95DC" }]}>{containersPageLevelList.length}</RNText>
        <RNText style={styles.metricLabel}>Containers</RNText>
      </TouchableOpacity>,
      <TouchableOpacity
        key="shipped"
        style={[
          styles.metricCard,
          { borderTopColor: "#10B981" },
          isMobileView ? { width: 85, marginRight: 8 } : { flex: 1 },
          statusFilters.includes("Shipped") && { backgroundColor: "#10B9811A", borderColor: "#10B981" }
        ]}
        onPress={() => {
          toggleFilter("Shipped", statusFilters, setStatusFilters);
          setFilterOpen(null);
        }}
      >
        <RNText style={[styles.metricNumber, { color: "#10B981" }]}>{shippedCount}</RNText>
        <RNText style={styles.metricLabel}>Shipped</RNText>
      </TouchableOpacity>,
      <TouchableOpacity
        key="scheduled"
        style={[
          styles.metricCard,
          { borderTopColor: "#F59E0B" },
          isMobileView ? { width: 85, marginRight: 8 } : { flex: 1 },
          statusFilters.includes("Scheduled") && { backgroundColor: "#F59E0B1A", borderColor: "#F59E0B" }
        ]}
        onPress={() => {
          toggleFilter("Scheduled", statusFilters, setStatusFilters);
          setFilterOpen(null);
        }}
      >
        <RNText style={[styles.metricNumber, { color: "#F59E0B" }]}>{scheduledCount}</RNText>
        <RNText style={styles.metricLabel}>Scheduled</RNText>
      </TouchableOpacity>
    ];

    if (isMobileView) {
      cards.push(
        <TouchableOpacity
          key="normal"
          style={[
            styles.metricCard,
            { borderTopColor: "#64748B" },
            isMobileView ? { width: 85, marginRight: 8 } : { flex: 1 },
            priorityFilters.includes("Normal") && { backgroundColor: "#64748B1A", borderColor: "#64748B" }
          ]}
          onPress={() => {
            toggleFilter("Normal", priorityFilters, setPriorityFilters);
            setFilterOpen(null);
          }}
        >
          <RNText style={[styles.metricNumber, { color: "#64748B" }]}>{normalCount}</RNText>
          <RNText style={styles.metricLabel}>Normal</RNText>
        </TouchableOpacity>
      );
      cards.push(
        <TouchableOpacity
          key="urgent"
          style={[
            styles.metricCard,
            { borderTopColor: "#EF4444" },
            isMobileView ? { width: 85, marginRight: 8 } : { flex: 1 },
            priorityFilters.includes("Urgent") && { backgroundColor: "#EF44441A", borderColor: "#EF4444" }
          ]}
          onPress={() => {
            toggleFilter("Urgent", priorityFilters, setPriorityFilters);
            setFilterOpen(null);
          }}
        >
          <RNText style={[styles.metricNumber, { color: "#EF4444" }]}>{urgentCount}</RNText>
          <RNText style={styles.metricLabel}>Urgent</RNText>
        </TouchableOpacity>
      );
      cards.push(
        <TouchableOpacity
          key="priority"
          style={[
            styles.metricCard,
            { borderTopColor: "#F59E0B" },
            isMobileView ? { width: 85, marginRight: 0 } : { flex: 1 },
            priorityFilters.includes("Priority") && { backgroundColor: "#F59E0B1A", borderColor: "#F59E0B" }
          ]}
          onPress={() => {
            toggleFilter("Priority", priorityFilters, setPriorityFilters);
            setFilterOpen(null);
          }}
        >
          <RNText style={[styles.metricNumber, { color: "#F59E0B" }]}>{priorityCount}</RNText>
          <RNText style={styles.metricLabel}>Priority</RNText>
        </TouchableOpacity>
      );
    }

    return <>{cards}</>;
  };

  return (
    <View style={{ flex: 1, backgroundColor: "#F1F5F9" }}>
      <ScrollView
        style={styles.scrollView}
        onScroll={onHandleScroll}
        ref={scrollRef}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
      >
        <View style={styles.pageWrapper}>

          {/* ── Metric Cards Row ────────────────────────── */}
          {isSmallDevice && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              bounces={false}
              overScrollMode="never"
              contentContainerStyle={{
                flexDirection: "row",
                paddingLeft: 16,
                paddingVertical: 12,
              }}
            >
              {renderMetricCards(true)}
              <View style={{ width: 16, backgroundColor: "transparent" }} />
            </ScrollView>
          )}

          {/* ── Table Content ──────────────────────────── */}
          <View style={[styles.tableCard, !isSmallDevice && { marginTop: 12 }]}>
            {isContainersListLoaded ? (
              <View>
                <FAB.Group
                  open={isFabOpen.open}
                  style={[
                    styles.fab,
                    {
                      paddingRight: isSmallDevice
                        ? "80%"
                        : isLargeDevice
                          ? "93%"
                          : "90%",
                    },
                  ]}
                  fabStyle={{ transform: [{ scale: 0.50 }] }}
                  icon={isFabOpen.open ? "plus" : "arrow-up"}
                  actions={[
                    {
                      icon: "plus",
                      onPress: () => {
                        window.location.href = isUserAdmin
                          ? `${oktaConfig.api.redirectUri}/containers/manage`
                          : "";
                      },
                    },
                    {
                      icon: "arrow-up",
                      onPress: onHandleScrollToBase,
                    },
                  ]}
                  visible={false}
                  onStateChange={onFabOpenChange}
                  onPress={() => {
                    if (isFabOpen.open) {
                      // do something if the speed dial is open
                    }
                  }}
                />
                <LabelBannerWithSearchBar
                  expandableSearchOnMobile={true}
                  titleName=""
                  titlePrefix={
                    <View style={{ flexDirection: "column" }}>
                      <View style={styles.quickFilterBar}>
                        <RNText style={styles.quickFilterLabel}>{t("quickFilter")}</RNText>
                        <TouchableOpacity
                          style={[
                            styles.mobileDropdownButton,
                            filterOpen === "status" && styles.mobileDropdownButtonActive,
                          ]}
                          onPress={() => setFilterOpen(filterOpen === "status" ? null : "status")}
                        >
                          <RNText style={styles.mobileDropdownLabel}>{t("status")}: </RNText>
                          <RNText style={styles.mobileDropdownValue}>
                            {statusFilters.length === 0 ? t("all") : statusFilters.map(s => t(s.toLowerCase())).join(", ")}
                          </RNText>
                          <RNText style={styles.mobileDropdownArrow}>
                            {filterOpen === "status" ? "▲" : "▼"}
                          </RNText>
                        </TouchableOpacity>

                        <TouchableOpacity
                          style={[
                            styles.mobileDropdownButton,
                            filterOpen === "priority" && styles.mobileDropdownButtonActive,
                            { marginLeft: 10 }
                          ]}
                          onPress={() => setFilterOpen(filterOpen === "priority" ? null : "priority")}
                        >
                          <RNText style={styles.mobileDropdownLabel}>{t("priority")}: </RNText>
                          <RNText style={styles.mobileDropdownValue}>
                            {priorityFilters.length === 0 ? t("all") : priorityFilters.map(p => t(p.toLowerCase())).join(", ")}
                          </RNText>
                          <RNText style={styles.mobileDropdownArrow}>
                            {filterOpen === "priority" ? "▲" : "▼"}
                          </RNText>
                        </TouchableOpacity>

                        <View
                          style={[
                            styles.mobileDropdownButton,
                            etdRangePickerOpen && styles.mobileDropdownButtonActive,
                            { marginLeft: 10, paddingRight: customEtdRange.startDate ? 4 : 12 }
                          ]}
                        >
                          <TouchableOpacity
                            style={{ flexDirection: "row", alignItems: "center" }}
                            onPress={() => setEtdRangePickerOpen(true)}
                          >
                            <RNText style={styles.mobileDropdownLabel}>{t("etd").toUpperCase()}: </RNText>
                            <RNText style={styles.mobileDropdownValue}>{getEtdDateRangeLabel()}</RNText>
                            {!customEtdRange.startDate && (
                              <RNText style={styles.mobileDropdownArrow}>
                                {etdRangePickerOpen ? "▲" : "▼"}
                              </RNText>
                            )}
                          </TouchableOpacity>
                          {customEtdRange.startDate && (
                            <TouchableOpacity
                              onPress={() => setCustomEtdRange({ startDate: undefined, endDate: undefined })}
                              style={{ paddingLeft: 6, paddingRight: 4 }}
                            >
                              <Ionicons name="close-circle" size={14} color="#94A3B8" />
                            </TouchableOpacity>
                          )}
                        </View>

                        <View
                          style={[
                            styles.mobileDropdownButton,
                            etaRangePickerOpen && styles.mobileDropdownButtonActive,
                            { marginLeft: 10, paddingRight: customEtaRange.startDate ? 4 : 12 }
                          ]}
                        >
                          <TouchableOpacity
                            style={{ flexDirection: "row", alignItems: "center" }}
                            onPress={() => setEtaRangePickerOpen(true)}
                          >
                            <RNText style={styles.mobileDropdownLabel}>{t("eta").toUpperCase()}: </RNText>
                            <RNText style={styles.mobileDropdownValue}>{getEtaDateRangeLabel()}</RNText>
                            {!customEtaRange.startDate && (
                              <RNText style={styles.mobileDropdownArrow}>
                                {etaRangePickerOpen ? "▲" : "▼"}
                              </RNText>
                            )}
                          </TouchableOpacity>
                          {customEtaRange.startDate && (
                            <TouchableOpacity
                              onPress={() => setCustomEtaRange({ startDate: undefined, endDate: undefined })}
                              style={{ paddingLeft: 6, paddingRight: 4 }}
                            >
                              <Ionicons name="close-circle" size={14} color="#94A3B8" />
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>

                      {filterOpen === "status" && (
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.quickFilterChipsContainer}>
                          {["All", "Shipped", "Scheduled"].map((status) => (
                            <TouchableOpacity
                              key={status}
                              style={[styles.filterChip, (status === "All" ? statusFilters.length === 0 : statusFilters.includes(status)) && styles.filterChipActive]}
                              onPress={() => toggleFilter(status, statusFilters, setStatusFilters)}
                            >
                              <RNText style={(status === "All" ? statusFilters.length === 0 : statusFilters.includes(status)) ? styles.filterChipTextActive : styles.filterChipText}>{t(status.toLowerCase())}</RNText>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      )}

                      {filterOpen === "priority" && (
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.quickFilterChipsContainer}>
                          {["All", "Urgent", "Normal", "Priority"].map((priority) => (
                            <TouchableOpacity
                              key={priority}
                              style={[styles.filterChip, (priority === "All" ? priorityFilters.length === 0 : priorityFilters.includes(priority)) && styles.filterChipActive]}
                              onPress={() => toggleFilter(priority, priorityFilters, setPriorityFilters)}
                            >
                              <RNText style={(priority === "All" ? priorityFilters.length === 0 : priorityFilters.includes(priority)) ? styles.filterChipTextActive : styles.filterChipText}>{t(priority.toLowerCase())}</RNText>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      )}


                    </View>
                  }
                  handleSearch={onHandleSearch}
                  setRefresh={setRefresh}
                  isResetFields={isRefresh}
                  extraControls={
                    <ContainerSearchDisplaySwitch
                      title={t("searchBy")}
                      isDisplayViewOn={_isDisplayViewOn}
                      setIsDisplayViewOn={_setIsDisplayViewOn}
                    />
                  }
                />
                {isSmallDevice ? (
                  <View style={cardStyles.cardList}>
                    {filteredContainersList.length === 0 ? (
                      <View style={cardStyles.emptyState}>
                        <Ionicons name="cube-outline" size={48} color="#CBD5E1" />
                        <RNText style={cardStyles.emptyStateText}>No containers found</RNText>
                      </View>
                    ) : (
                      filteredContainersList.map((container) => (
                        <MobileContainerCard key={container._id || container.container_number} container={container} />
                      ))
                    )}
                  </View>
                ) : (
                  <DataTableWithPagination
                    headers={tableHeaders}
                    rows={filteredContainersList}
                    itemsPerPage={20}
                    isDataLoading={isSearched}
                    useModal={true}
                    nestedHeader={nestedTableHeaders}
                    isRowsExpandable={true}
                    setModalVisible={setModalVisible}
                    handleRowClick={onHandleRowClick}
                    setSelected={setSelected}
                    isToExpandAllRows={_isDisplayViewOn}
                    rowKeyValue="container_number"
                    nestedItemKeyValue="items"
                    isDetail={true}
                    isEdit={isUserAdmin}
                    isDelete={isUserAdmin}
                    columnsToNotHaveThousandSeparator={
                      columnsToNotHaveThousandsSeparator
                    }
                    handleDeleteRow={onHandleDeleteRow}
                    handleSortColumn={onHandleSortColumn}
                  />
                )}
              </View>
            ) : (
              <View style={styles.loaderContainer}>
                <View style={styles.loaderInner}>
                  <Spinner
                    size="small"
                    color="#2F95DC"
                    styles={{ alignSelf: "center" }}
                  />
                  <Text style={styles.loaderText}>Loading containers...</Text>
                </View>
              </View>
            )}
          </View>

          <Modal.ModalContainer
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            childern={ModalContent}
          />
          <MobileItemsModal />

          <PaperProvider
            theme={{
              ...DefaultTheme,
              colors: {
                ...DefaultTheme.colors,
                primary: "#3B82F6",
                primaryContainer: "#DBEAFE",
                onPrimaryContainer: "#1E40AF",
                secondaryContainer: "#BFDBFE",
                onSecondaryContainer: "#1E3A5F",
                onSurface: "#1E293B",
                onSurfaceVariant: "#475569",
              },
            }}
          >
            <DatePickerModal
              locale="en-AU"
              mode="range"
              visible={etdRangePickerOpen}
              onDismiss={() => setEtdRangePickerOpen(false)}
              startDate={customEtdRange.startDate}
              endDate={customEtdRange.endDate}
              onConfirm={({ startDate, endDate }: any) => {
                setEtdRangePickerOpen(false);
                if (startDate) {
                  startDate.setHours(0, 0, 0, 0);
                  const finalEndDate = endDate ? new Date(endDate) : new Date(startDate);
                  finalEndDate.setHours(0, 0, 0, 0);
                  setCustomEtdRange({ startDate, endDate: finalEndDate });
                } else {
                  setCustomEtdRange({ startDate: undefined, endDate: undefined });
                }
              }}
            />
            <DatePickerModal
              locale="en-AU"
              mode="range"
              visible={etaRangePickerOpen}
              onDismiss={() => setEtaRangePickerOpen(false)}
              startDate={customEtaRange.startDate}
              endDate={customEtaRange.endDate}
              onConfirm={({ startDate, endDate }: any) => {
                setEtaRangePickerOpen(false);
                if (startDate) {
                  startDate.setHours(0, 0, 0, 0);
                  const finalEndDate = endDate ? new Date(endDate) : new Date(startDate);
                  finalEndDate.setHours(0, 0, 0, 0);
                  setCustomEtaRange({ startDate, endDate: finalEndDate });
                } else {
                  setCustomEtaRange({ startDate: undefined, endDate: undefined });
                }
              }}
            />
          </PaperProvider>
        </View>
      </ScrollView>
      {isSmallDevice && contentOffSet > 200 && (
        <FAB
          customSize={36}
          style={{
            position: "absolute",
            right: 16,
            bottom: 16,
            backgroundColor: "#2F95DC",
            opacity: 0.75,
          }}
          icon="arrow-up"
          color="white"
          onPress={onHandleScrollToBase}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: "#F1F5F9",
  },
  pageWrapper: {
    flex: 1,
    backgroundColor: "#F1F5F9",
    minHeight: "100%" as any,
  },

  // ── Top Bar ─────────────────────────────────────
  topBar: {
    backgroundColor: "#1E293B",
    paddingHorizontal: 24,
    paddingVertical: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  topBarLeft: {
    gap: 4,
  },
  topBarTitle: {
    fontSize: 22,
    fontWeight: "700",
    color: "#F8FAFC",
    letterSpacing: -0.3,
  },
  topBarSub: {
    fontSize: 13,
    color: "#94A3B8",
    fontWeight: "400",
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#2F95DC",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },

  metricRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
    backgroundColor: "transparent",
  },

  // ── Quick Filters ────────────────────────────────
  quickFilterBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
  },
  quickFilterChipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 8,
  },
  quickFilterLabel: {
    fontWeight: "700",
    fontSize: 13,
    color: "#374151",
    marginRight: 10,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  filterChipActive: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterChipText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500" as const,
  },
  filterChipTextActive: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600" as const,
  },
  metricCard: {
    minWidth: 80,
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    padding: 12,
    borderTopWidth: 3,
    borderWidth: 1,
    borderColor: "transparent",
    shadowColor: "#64748B",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  metricCardActive: {
    backgroundColor: "#F8FAFC",
    borderColor: "#E2E8F0",
  },
  metricNumber: {
    fontSize: 22,
    fontWeight: "800",
    marginBottom: 2,
  },
  metricLabel: {
    fontSize: 11,
    color: "#64748B",
    fontWeight: "500",
  },

  // ── Table Card ───────────────────────────────────
  tableCard: {
    marginHorizontal: 16,
    marginBottom: 24,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    overflow: "hidden" as any,
    shadowColor: "#0F172A",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 4,
  },

  // ── Loader ───────────────────────────────────────
  loaderContainer: {
    padding: 60,
    alignItems: "center",
  },
  loaderInner: {
    alignItems: "center",
    gap: 12,
  },
  loaderText: {
    fontSize: 14,
    color: "#94A3B8",
    fontWeight: "500",
  },

  // Legacy (keep for FAB)
  fab: {
    bottom: -10,
    position: "absolute",
    zIndex: 1,
  },
  container: {},
  containerContent: {},
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
});

const cardStyles = StyleSheet.create({
  // ── Card List ─────────────────────────────────────
  cardList: {
    paddingHorizontal: 16,
    paddingBottom: 24,
    paddingTop: 8,
    gap: 12,
    backgroundColor: "transparent",
  },

  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E8EDF5",
    shadowColor: "#1E293B",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.07,
    shadowRadius: 10,
    elevation: 3,
    overflow: "hidden" as any,
    marginBottom: 4,
  },

  // ── Card Header ────────────────────────────────────
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: "#FAFBFF",
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: "transparent",
  },
  containerIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: "#E1F0FA",
    alignItems: "center",
    justifyContent: "center",
  },
  containerNumber: {
    fontSize: 15,
    fontWeight: "700" as const,
    color: "#0F172A",
    letterSpacing: 0.3,
  },

  // ── Status Badge ───────────────────────────────────
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 999,
    gap: 5,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600" as const,
  },

  // ── Divider ────────────────────────────────────────
  divider: {
    height: 1,
    backgroundColor: "#F1F5F9",
    marginHorizontal: 16,
  },

  // ── Date Timeline ──────────────────────────────────
  dateRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 4,
  },
  dateBlock: {
    flex: 1,
    alignItems: "center",
    gap: 3,
  },
  dateLabel: {
    fontSize: 10,
    color: "#94A3B8",
    fontWeight: "600" as const,
    textTransform: "uppercase" as any,
    letterSpacing: 0.5,
  },
  dateValue: {
    fontSize: 13,
    color: "#1E293B",
    fontWeight: "700" as const,
  },

  // ── Meta Chips ─────────────────────────────────────
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap" as any,
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 6,
  },
  metaChip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8FAFC",
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    gap: 4,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    flexShrink: 1,
  },
  metaChipText: {
    fontSize: 11,
    color: "#6B7280",
    fontWeight: "500" as const,
    flexShrink: 1,
  },

  // ── Progress ───────────────────────────────────────
  progressSection: {
    paddingHorizontal: 16,
    paddingBottom: 14,
    gap: 5,
  },
  progressLabelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  progressLabel: {
    fontSize: 11,
    color: "#94A3B8",
    fontWeight: "500" as const,
  },
  progressPct: {
    fontSize: 11,
    color: "#2F95DC",
    fontWeight: "700" as const,
  },
  progressTrack: {
    height: 6,
    backgroundColor: "#E1F0FA",
    borderRadius: 999,
    overflow: "hidden" as any,
  },
  progressFill: {
    height: 6,
    backgroundColor: "#2F95DC",
    borderRadius: 999,
  },

  // ── Footer ─────────────────────────────────────────
  cardFooter: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap" as any,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: "#F1F5F9",
    backgroundColor: "#FAFBFF",
    gap: 8,
  },
  priorityBadge: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 6,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderWidth: 1,
    gap: 4,
  },
  priorityDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
  },
  priorityText: {
    fontSize: 11,
    fontWeight: "600" as const,
  },
  itemsCount: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginLeft: 4,
  },
  itemsCountText: {
    fontSize: 11,
    color: "#2F95DC",
    fontWeight: "500" as const,
  },
  cardActions: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    backgroundColor: "#FAFBFF",
    gap: 8,
  },
  viewBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#2F95DC",
    backgroundColor: "#FAFBFF",
  },
  viewBtnText: {
    fontSize: 12,
    color: "#2F95DC",
    fontWeight: "600" as const,
  },
  editBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: "#2F95DC",
  },
  editBtnText: {
    fontSize: 12,
    color: "#FFFFFF",
    fontWeight: "600" as const,
  },

  // ── Empty State ────────────────────────────────────
  emptyState: {
    alignItems: "center",
    paddingVertical: 60,
    gap: 12,
  },
  emptyStateText: {
    fontSize: 15,
    color: "#CBD5E1",
    fontWeight: "500" as const,
  },
});

const itemModalStyles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#F8FAFC",
  },

  // ── Header ─────────────────────────────────────────
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 18,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E8EDF5",
    shadowColor: "#1E293B",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 6,
    elevation: 2,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: "#E1F0FA",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "700" as const,
    color: "#0F172A",
    letterSpacing: 0.2,
  },
  headerSub: {
    fontSize: 12,
    color: "#94A3B8",
    fontWeight: "500" as const,
    marginTop: 1,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#F1F5F9",
    alignItems: "center",
    justifyContent: "center",
  },

  // ── List ───────────────────────────────────────────
  list: {
    flex: 1,
  },
  listContent: {
    padding: 16,
    gap: 12,
    paddingBottom: 40,
  },

  // ── Item Card ──────────────────────────────────────
  itemCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#E8EDF5",
    padding: 16,
    shadowColor: "#1E293B",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    gap: 10,
    marginBottom: 4,
  },
  itemCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  itemCodeWrap: {
    backgroundColor: "#E1F0FA",
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  itemCode: {
    fontSize: 13,
    fontWeight: "700" as const,
    color: "#1D6FA5",
    letterSpacing: 0.3,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 999,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600" as const,
  },
  description: {
    fontSize: 13,
    color: "#374151",
    fontWeight: "500" as const,
    lineHeight: 19,
  },
  detailRow: {
    flexDirection: "row",
    flexWrap: "wrap" as any,
    gap: 6,
  },
  detailChip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8FAFC",
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    gap: 4,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  detailChipText: {
    fontSize: 11,
    color: "#6B7280",
    fontWeight: "500" as const,
  },

  // ── Empty State ────────────────────────────────────
  emptyState: {
    alignItems: "center",
    paddingVertical: 80,
    gap: 14,
  },
  emptyText: {
    fontSize: 15,
    color: "#CBD5E1",
    fontWeight: "500" as const,
  },
});
